import * as API from '@siloreal:shared:api';

const SILOBAG_LIST_ENDPOINT = '/v3/silobags';

export type FilterInteraction = 'eq' | 'contain-text' | 'contain-array' | 'between';
export type SortDirection = 'asc' | 'desc';
export type QueryValue = string | number | boolean | Date;
export type RangeFilterValue = {
  start?: QueryValue | null;
  end?: QueryValue | null;
};

const SILOBAG_LIST_FIELD_CAPABILITIES = {
  name: { filter: 'contain-text', sorting: true },
  id: { filter: 'contain-text' },
  silobag_species_type_id: { filter: 'contain-array', sorting: true },
  silobag_product_type_id: { filter: 'contain-array', sorting: true },
  created_at: { filter: 'between', sorting: true },
  silobag_harvest_id: { filter: 'contain-array' },
  bagging_date: { filter: 'between', sorting: true },
  initial_humidity: { filter: 'between', sorting: true },
  remaining_stock: { filter: 'between', sorting: true },
  total_extracted_weight: { filter: 'between', sorting: true },
  organization_id: { filter: 'contain-array', sorting: true },
  company_id: { filter: 'contain-array' },
  countryside_id: { filter: 'contain-array', sorting: true },
  lot_id: { filter: 'contain-array' },
  countryside_zone_id: { filter: 'contain-array' },
  color_id: { filter: 'contain-array' },
  sat_view_active: { filter: 'eq' },
  is_silo_metrix_active: { filter: 'eq' },
  active: { filter: 'eq' },
  corvus: { filter: 'eq' },
  vrr: { filter: 'eq' },
  silobag_data_status_id: { filter: 'eq' },
  inconsistencies: { filter: 'eq' },
  tag_id: { filter: 'contain-array' },
} as const satisfies Record<string, { filter?: FilterInteraction; sorting?: true }>;

export type SilobagListField = keyof typeof SILOBAG_LIST_FIELD_CAPABILITIES;
export type SilobagListFilterValue = QueryValue | QueryValue[] | RangeFilterValue | undefined | null;
export type SilobagListFilter = Partial<Record<SilobagListField, SilobagListFilterValue>>;
export type SilobagListPagination = {
  page: number;
  take: number;
};
export type SilobagListSort = Array<{
  field: SilobagListField;
  direction: SortDirection;
}>;

export type SilobagListServiceContract = {
  pagination?: SilobagListPagination;
  sort?: SilobagListSort;
  search?: string;
  filter?: SilobagListFilter;
  exactIds?: readonly string[];
  signal?: AbortSignal;
  onlyIds?: boolean;
};

export class SilobagListEndpointMisusedError extends Error {
  constructor(message: string) {
    super(`endpoint mal utilizado: ${message}`);
    this.name = 'SilobagListEndpointMisusedError';
  }
}

export async function listSilobagsService(
  contract: SilobagListServiceContract & { onlyIds: true }
): Promise<SilobagListOnlyIdsResponse>;
export async function listSilobagsService(
  contract?: SilobagListServiceContract
): Promise<SilobagListResponse>;
export async function listSilobagsService(
  contract: SilobagListServiceContract = {}
): Promise<SilobagListResponse | SilobagListOnlyIdsResponse> {
  const query = buildSilobagListQuery(contract);
  const { data } = await API.http.fetch.get<SilobagListResponse | SilobagListOnlyIdsResponse>(
    SILOBAG_LIST_ENDPOINT,
    {
      query,
      signal: contract.signal,
    }
  );

  return data;
}

export function buildSilobagListQuery(contract: SilobagListServiceContract): string {
  const queryParameters = mergeQueryParameters(
    paginationToQueryParameters(contract.pagination),
    filterToQueryParameters(contract.filter),
    searchToQueryParameters(contract.search, contract.filter),
    sortToQueryParameters(contract.sort),
    exactIdsToQueryParameters(contract.exactIds),
    onlyIdsToQueryParameters(contract.onlyIds)
  );

  return queryParameters ? `?${queryParameters}` : '';
}

export function onlyIdsToQueryParameters(onlyIds?: boolean): string {
  if (!onlyIds) return '';

  const queryParameters = new URLSearchParams();

  queryParameters.set('only_ids', 'true');

  return queryParameters.toString();
}

export function exactIdsToQueryParameters(exactIds?: readonly string[]): string {
  const normalizedIds = (exactIds ?? []).map((id) => id.trim()).filter(Boolean);
  if (normalizedIds.length === 0) return '';

  const queryParameters = new URLSearchParams();

  queryParameters.set('exact_ids', normalizedIds.join(','));

  return queryParameters.toString();
}

export function paginationToQueryParameters(pagination?: SilobagListPagination): string {
  if (!pagination) return '';

  const queryParameters = new URLSearchParams();

  queryParameters.set('page', serializePaginationNumber('pagination.page', pagination.page));
  queryParameters.set('take', serializePaginationNumber('pagination.take', pagination.take));

  return queryParameters.toString();
}

export function filterToQueryParameters(filter?: SilobagListFilter): string {
  const queryParameters = new URLSearchParams();

  Object.entries(filter ?? {}).forEach(([field, value]) => {
    const interaction = getFilterInteraction(field);

    if (isEmptyFilterValue(value)) return;

    if (interaction === 'contain-text' || interaction === 'eq') {
      queryParameters.set(field, serializeScalarValue(field, value));
      return;
    }

    if (interaction === 'contain-array') {
      const values = serializeArrayFilterValue(field, value);
      const queryKey = Array.isArray(value) ? toPluralIdField(field) : field;

      queryParameters.set(queryKey, values.join(','));
      return;
    }

    const range = serializeRangeFilterValue(field, value);

    if (range.start) queryParameters.set(`start_${field}`, range.start);
    if (range.end) queryParameters.set(`end_${field}`, range.end);
  });

  return queryParameters.toString();
}

export function sortToQueryParameters(sort?: SilobagListSort): string {
  const queryParameters = new URLSearchParams();

  (sort ?? []).forEach((sortItem) => {
    assertValidSortDirection(sortItem.direction);
    assertCanSort(sortItem.field);
    queryParameters.append('sorting', `${sortItem.field}:${sortItem.direction}`);
  });

  return queryParameters.toString();
}

function searchToQueryParameters(search: string | undefined, filter: SilobagListFilter | undefined): string {
  const normalizedSearch = search?.trim();

  if (!normalizedSearch || hasQueryValue(filter?.name)) return '';

  return filterToQueryParameters({ name: normalizedSearch, id: normalizedSearch });
}

function mergeQueryParameters(...queryParameterStrings: string[]): string {
  const mergedQueryParameters = new URLSearchParams();

  queryParameterStrings.forEach((queryParameterString) => {
    const queryParameters = new URLSearchParams(queryParameterString);

    queryParameters.forEach((value, key) => {
      mergedQueryParameters.append(key, value);
    });
  });

  return mergedQueryParameters.toString();
}

function getFilterInteraction(field: string): FilterInteraction {
  const capability = getFieldCapability(field);

  if (!capability.filter) {
    throw new SilobagListEndpointMisusedError(`la propiedad '${field}' no admite filtering`);
  }

  return capability.filter;
}

function assertCanSort(field: string): void {
  const capability = getFieldCapability(field);

  if (!('sorting' in capability)) {
    throw new SilobagListEndpointMisusedError(`la propiedad '${field}' no admite sorting`);
  }
}

function getFieldCapability(field: string): (typeof SILOBAG_LIST_FIELD_CAPABILITIES)[SilobagListField] {
  if (!isSilobagListField(field)) {
    throw new SilobagListEndpointMisusedError(`la propiedad '${field}' no existe en este endpoint`);
  }

  return SILOBAG_LIST_FIELD_CAPABILITIES[field];
}

function isSilobagListField(field: string): field is SilobagListField {
  return field in SILOBAG_LIST_FIELD_CAPABILITIES;
}

function assertValidSortDirection(sortDirection: unknown): asserts sortDirection is SortDirection {
  if (sortDirection !== 'asc' && sortDirection !== 'desc') {
    throw new SilobagListEndpointMisusedError(
      `la dirección de sorting '${String(sortDirection)}' no es válida`
    );
  }
}

function serializePaginationNumber(field: string, value: number): string {
  if (!Number.isFinite(value)) {
    throw new SilobagListEndpointMisusedError(`la propiedad '${field}' recibió un número inválido`);
  }

  if (!Number.isInteger(value)) {
    throw new SilobagListEndpointMisusedError(`la propiedad '${field}' debe ser un número entero`);
  }

  if (field === 'pagination.page' && value < 0) {
    throw new SilobagListEndpointMisusedError(`la propiedad '${field}' no puede ser negativa`);
  }

  if (field === 'pagination.take' && value <= 0) {
    throw new SilobagListEndpointMisusedError(`la propiedad '${field}' debe ser mayor a cero`);
  }

  return String(value);
}

function serializeScalarValue(field: string, value: SilobagListFilterValue): string {
  if (!isQueryValue(value)) {
    throw new SilobagListEndpointMisusedError(`la propiedad '${field}' espera un valor simple`);
  }

  return serializeQueryValue(field, value);
}

function serializeArrayFilterValue(field: string, value: SilobagListFilterValue): string[] {
  const values = Array.isArray(value) ? value : [value];
  const serializedValues = values.reduce<string[]>((currentValues, item) => {
    if (isEmptyFilterValue(item)) return currentValues;

    return [...currentValues, serializeScalarValue(field, item)];
  }, []);

  if (serializedValues.length === 0) {
    throw new SilobagListEndpointMisusedError(`la propiedad '${field}' no recibió valores válidos`);
  }

  return serializedValues;
}

function serializeRangeFilterValue(
  field: string,
  value: SilobagListFilterValue
): { start?: string; end?: string } {
  const range = Array.isArray(value) ? { start: value[0], end: value[1] } : value;

  if (!isRangeFilterValue(range)) {
    throw new SilobagListEndpointMisusedError(`la propiedad '${field}' espera un rango start/end`);
  }

  const start = isEmptyFilterValue(range.start) ? undefined : serializeScalarValue(field, range.start);
  const end = isEmptyFilterValue(range.end) ? undefined : serializeScalarValue(field, range.end);

  if (!start && !end) {
    throw new SilobagListEndpointMisusedError(`la propiedad '${field}' no recibió un rango válido`);
  }

  return { start, end };
}

function serializeQueryValue(field: string, value: QueryValue): string {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      throw new SilobagListEndpointMisusedError(`la propiedad '${field}' recibió una fecha inválida`);
    }

    return value.toISOString();
  }

  if (typeof value === 'number' && !Number.isFinite(value)) {
    throw new SilobagListEndpointMisusedError(`la propiedad '${field}' recibió un número inválido`);
  }

  return String(value).trim();
}

function hasQueryValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.some(hasQueryValue);
  return true;
}

function isEmptyFilterValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  return false;
}

function isQueryValue(value: unknown): value is QueryValue {
  if (value instanceof Date) return true;
  const valueType = typeof value;

  return valueType === 'string' || valueType === 'number' || valueType === 'boolean';
}

function isRangeFilterValue(value: unknown): value is RangeFilterValue {
  return value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date);
}

function toPluralIdField(field: string): string {
  if (field.endsWith('_id')) return `${field.slice(0, -3)}_ids`;

  return `${field}s`;
}

export type SilobagListResponse = {
  page?: number;
  take?: number;
  total: number;
  rowCount: number;
  data: SilobagListItem[];
};

export type SilobagListOnlyIdsResponse = {
  ids: SilobagListItem['id'][];
  total: number;
};

type NullableRelation<T> = T | null;

export type SilobagListItem = {
  id: string;
  name: string;
  size: string;
  input_weight: string;
  is_corvus_weight: boolean;
  weight_before_corvus: string;
  main_lat: string;
  main_lon: string;
  bagging_date: string;
  organization_id: number;
  company_id: number;
  companies: NullableRelation<SilobagCompany>;
  active: boolean;
  partially_extracted: boolean;
  extracted_all: boolean;
  variety: string;
  calculated_weight: string | null;
  initial_humidity: number | null;
  responsible_for_bagging: string;
  split_grain_percentage: number | null;
  varietal_purity: number | null;
  fillying_coefficient: number | null;
  uuid: string;
  observations: string;
  silobag_brand: string;
  created_at: string;
  last_extraction_date: string | null;
  lots: NullableRelation<SilobagLot>;
  silobag_product_types: NullableRelation<SilobagProductType>;
  silobag_species_types: NullableRelation<SilobagSpeciesType>;
  humidity_sources: unknown | null;
  silobag_data_statuses: NullableRelation<SilobagDataStatus>;
  weight_sources: unknown | null;
  silobag_harvests: NullableRelation<SilobagHarvest>;
  organizations: NullableRelation<SilobagOrganization>;
  v_silobags_services_statuses: NullableRelation<SilobagServicesStatus>;
  v_silobags_bagging_date_check: SilobagBaggingDateCheck[];
  v_silobag_closing_dates: unknown | null;
  v_is_silobag_available_for_closure: NullableRelation<SilobagAvailabilityForClosure>;
  v_archivable_silobags: NullableRelation<SilobagArchivableStatus>;
  _count: NullableRelation<SilobagCounters>;
  read_permission: boolean;
  write_permission: boolean;
  full_permission: boolean;
  is_shared_externally?: boolean;
  is_guest: boolean;
  inherited: boolean;
  total_extracted_weight: string;
  remaining_stock: string;
  hex: string;
  alert_value: number | null;
  short_name_last_alert: string | null;
  severity: number;
  // silobags_origin_lots: unknown[];
  disc_discrepancies_results: DiscrepancyResult[];
  // lab_protocols: unknown[];
  // v_silobags_existence_states: unknown[];
  tags_silobags: SilobagTagItem[];
  groupedTags: SilobagGroupedTags[];
};

type DiscrepancyResult = {
  id: number;
  rule: { level: number };
};

type SilobagTagItem = { tag: Tag };
type SilobagGroupedTags = {
  scope: TagScopeValue;
  tags: Tag[];
  translations: Array<{ language: TagLanguage; value: string }>;
};
type Tag = {
  id: number;
  label: string;
  bg_color: string;
  text_color: string;
  scope: TagScope;
  translations: TagTranslation[];
  active: boolean;
  created_at: ISODateString;
  updated_at: ISODateString;
  fileTypeTags: unknown[];
};
type TagScope = { value: TagScopeValue };
type ISODateString = string;
type TagScopeValue = 'system' | 'organization';
type TagTranslation = { language: TagLanguage; value: string; tooltip: string };
type TagLanguage = 'es' | 'en' | 'pt';
type SilobagOrganization = { id: number; name: string };

type SilobagCompany = {
  id: number;
  name: string;
};

type SilobagLot = {
  id: number;
  name: string;
  countrysides: {
    id: number;
    name: string;
    countryside_zone: unknown | null;
  };
};

type SilobagProductType = {
  id: number;
  name: string;
  active: boolean;
  created_at: string;
  updated_at: string | null;
  silobag_product_types_translations: SilobagProductTypeTranslation[];
};

type SilobagProductTypeTranslation = {
  id: number;
  silobag_product_types_id: number;
  language_id: number;
  description: string;
  created_at: string;
  updated_at: string | null;
};

type SilobagSpeciesType = {
  id: number;
  name: string;
  active: boolean;
  created_at: string;
  updated_at: string | null;
  reference: string | null;
  const_a: string | null;
  const_b: string | null;
  const_c: string | null;
  image_url: string;
  min_filling_factor_tn: string;
  max_filling_factor_tn: string;
  silobag_species_types_translations: SilobagSpeciesTypeTranslation[];
};

type SilobagSpeciesTypeTranslation = {
  id: number;
  silobag_species_type_id: number;
  language_id: number;
  description: string;
  created_at: string;
  updated_at: string | null;
};

type SilobagDataStatus = {
  id: number;
  name: string;
};

type SilobagHarvest = {
  id: number;
  name: string;
  start_year: number | null;
  end_year: number | null;
  active: boolean;
  created_at: string;
  updated_at: string | null;
};

type SilobagServicesStatus = {
  silobag_id: string;
  sat_view_active: boolean;
  sat_view_state: string;
  sat_view_observations: string | null;
  silo_metrix_active: boolean;
  tesma_integration: boolean;
};

type SilobagBaggingDateCheck = {
  silobag_id: string;
  bagging_date_check: string | null;
};

type SilobagAvailabilityForClosure = {
  result: boolean;
};

type SilobagArchivableStatus = {
  can_archive: boolean;
};

type SilobagCounters = {
  lab_protocols: number;
};
